import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { API, useLanguage } from "../App";
import axios from "axios";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { 
  Clock, Check, Lock, Sparkles, MapPin, Plus,
  Mail, Phone, Instagram, Facebook, ChevronLeft, ChevronRight, Play, Pause
} from "lucide-react";
import { InfoPopover } from "../components/InfoPopover";

export default function PublicProposal() {
  const { proposalId } = useParams();
  const { language, setLanguage, t, availableLanguages, setProposalLanguages, LANGUAGES } = useLanguage();
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requiresPassword, setRequiresPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(true);
  
  // Parallax effect refs
  const heroRef = useRef(null);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 500], [0, 150]);
  const heroScale = useTransform(scrollY, [0, 500], [1, 1.1]);
  const heroOpacity = useTransform(scrollY, [0, 400], [1, 0.3]);

  useEffect(() => {
    fetchProposal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalId]);

  useEffect(() => {
    if (proposal?.offer_end) {
      const timer = setInterval(() => {
        const end = new Date(proposal.offer_end);
        const now = new Date();
        const diff = end - now;
        
        if (diff <= 0) {
          setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
          clearInterval(timer);
          return;
        }
        
        setCountdown({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((diff % (1000 * 60)) / 1000)
        });
      }, 1000);
      
      return () => clearInterval(timer);
    }
  }, [proposal?.offer_end]);

  // Auto-rotate hero images
  useEffect(() => {
    if (proposal?.hero_images?.length > 1) {
      const interval = setInterval(() => {
        setCurrentImageIndex(prev => 
          prev === proposal.hero_images.length - 1 ? 0 : prev + 1
        );
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [proposal?.hero_images]);

  const fetchProposal = async (pwd = null) => {
    try {
      const url = pwd 
        ? `${API}/public/proposals/${proposalId}?password=${encodeURIComponent(pwd)}`
        : `${API}/public/proposals/${proposalId}`;
      
      const res = await axios.get(url);
      
      if (res.data.requires_password) {
        setRequiresPassword(true);
        setProposal({ hotel_name: res.data.hotel_name, proposal_id: res.data.proposal_id });
      } else {
        setRequiresPassword(false);
        setProposal(res.data);
        
        // Set available languages based on detected language
        const detectedLang = res.data.detected_language || "de";
        setProposalLanguages(detectedLang);
        
        // Auto-select popular plan
        const plans = getPlans(res.data);
        const popular = plans.find(p => p.plan.is_popular);
        if (popular) setSelectedPlan(popular.key);
        else if (plans.length > 0) setSelectedPlan(plans[1]?.key || plans[0]?.key);
      }
    } catch (error) {
      console.error("Failed to load proposal");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    fetchProposal(password);
  };

  const getPlans = (p) => {
    const plans = [];
    if (p?.plan_a?.name) plans.push({ key: 'A', plan: p.plan_a });
    if (p?.plan_b?.name) plans.push({ key: 'B', plan: p.plan_b });
    if (p?.plan_c?.name) plans.push({ key: 'C', plan: p.plan_c });
    if (p?.plan_d?.name) plans.push({ key: 'D', plan: p.plan_d });
    return plans;
  };

  const toggleAddOn = (addon) => {
    setSelectedAddOns(prev => {
      // Remove if already selected
      if (prev.find(a => a.id === addon.id && !a.package_id)) {
        return prev.filter(a => !(a.id === addon.id && !a.package_id));
      }
      // Add new addon (mark as individual, not from package)
      return [...prev, { ...addon, package_id: null }];
    });
  };

  const togglePackage = (pkg) => {
    setSelectedAddOns(prev => {
      // Check if this package is already selected
      const hasPackage = prev.some(a => a.package_id === pkg.id);
      
      if (hasPackage) {
        // Remove all items from this package
        return prev.filter(a => a.package_id !== pkg.id);
      } else {
        // Remove any individual add-ons that are in this package first
        const filteredPrev = prev.filter(a => !pkg.addon_ids.includes(a.id) || a.package_id);
        
        // Add package as a single item with bundled price
        return [...filteredPrev, {
          id: `pkg_${pkg.id}`,
          package_id: pkg.id,
          name_en: pkg.name_en,
          name_de: pkg.name_de,
          description_en: pkg.description_en,
          description_de: pkg.description_de,
          info_video_url: pkg.info_video_url,
          price: pkg.package_price,
          is_package: true
        }];
      }
    });
  };

  const calculateTotal = () => {
    if (!selectedPlan || !proposal) return 0;
    const planKey = `plan_${selectedPlan.toLowerCase()}`;
    const planPrice = proposal?.[planKey]?.price || 0;
    const addOnsPrice = selectedAddOns.reduce((sum, addon) => sum + (addon.price || 0), 0);
    let total = planPrice + addOnsPrice;
    
    if (proposal?.discount_percent > 0) {
      total = total * (1 - proposal.discount_percent / 100);
    }
    
    return total.toFixed(0);
  };

  const handleCheckout = async () => {
    try {
      await axios.post(`${API}/public/proposals/${proposalId}/convert`);
      const planKey = `plan_${selectedPlan.toLowerCase()}`;
      const shopifyLink = proposal?.[planKey]?.shopify_link;
      
      if (shopifyLink) {
        window.location.href = shopifyLink;
      } else {
        alert(t("Checkout coming soon!", "Checkout kommt bald!"));
      }
    } catch (error) {
      console.error("Failed to track conversion");
    }
  };

  const nextImage = () => {
    if (proposal?.hero_images?.length > 1) {
      setCurrentImageIndex(prev => 
        prev === proposal.hero_images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (proposal?.hero_images?.length > 1) {
      setCurrentImageIndex(prev => 
        prev === 0 ? proposal.hero_images.length - 1 : prev - 1
      );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="gradient-bg" />
        <div className="spinner" />
      </div>
    );
  }

  if (requiresPassword) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="gradient-bg" />
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass-card-static p-8 max-w-md w-full text-center"
        >
          <Lock className="w-12 h-12 text-[#C9A96E] mx-auto mb-4" />
          <h2 className="font-display text-2xl text-white mb-2">{t("Protected Proposal", "Geschützter Vorschlag")}</h2>
          <p className="text-white/50 mb-6">{proposal?.hotel_name}</p>
          
          <form onSubmit={handlePasswordSubmit}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("Enter password", "Passwort eingeben")}
              className="form-input mb-4"
              autoFocus
              data-testid="proposal-password-input"
            />
            <button
              type="submit"
              className="btn-accent w-full"
              data-testid="proposal-password-submit"
            >
              {t("Access Proposal", "Vorschlag öffnen")}
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  if (!proposal) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="gradient-bg" />
        <div className="text-center">
          <h1 className="font-display text-3xl text-white mb-4">{t("Not Found", "Nicht gefunden")}</h1>
        </div>
      </div>
    );
  }

  const plans = getPlans(proposal);
  const addOns = proposal.add_ons || [];
  const isExpired = proposal.status === "expired";
  const heroImages = proposal.hero_images || [];
  const hasImages = heroImages.length > 0;

  // Get personalized message based on current language
  const getPersonalizedMessage = () => {
    if (!proposal) return "";
    const messages = {
      en: proposal.personalized_message_en || proposal.intro_message_en,
      de: proposal.personalized_message_de || proposal.intro_message_de,
      fr: proposal.personalized_message_fr,
      es: proposal.personalized_message_es,
      it: proposal.personalized_message_it,
      th: proposal.personalized_message_th
    };
    return messages[language] || messages.en || "";
  };

  return (
    <div className="min-h-screen relative">
      <div className="gradient-bg" />
      
      {/* Language Toggle - Fixed - Dynamic based on detected language */}
      <div className="fixed top-4 right-4 z-50">
        <div className="lang-toggle">
          {availableLanguages.map(lang => (
            <button
              key={lang}
              onClick={() => setLanguage(lang)}
              className={language === lang ? "active" : ""}
              data-testid={`lang-${lang}-btn`}
              title={LANGUAGES[lang]?.name}
            >
              {lang.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* HERO SECTION with Parallax & Video Support */}
      {hasImages && (
        <section ref={heroRef} className="relative h-[60vh] lg:h-[70vh] overflow-hidden" data-testid="hero-section">
          {/* Background with Parallax */}
          <motion.div 
            style={{ y: heroY, scale: heroScale }}
            className="absolute inset-0 will-change-transform"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={currentImageIndex}
                initial={{ opacity: 0, scale: 1.05 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.2, ease: "easeOut" }}
                className="absolute inset-0"
              >
                {/* Check if it's a video */}
                {heroImages[currentImageIndex]?.match(/\.(mp4|webm|mov)$/i) ? (
                  <div className="relative w-full h-full">
                    <video 
                      src={heroImages[currentImageIndex]} 
                      autoPlay 
                      muted 
                      loop 
                      playsInline
                      className="w-full h-full object-cover"
                      style={{ transform: 'scale(1.1)' }}
                    />
                    {/* Video label */}
                    <div className="absolute top-6 left-6 z-20">
                      <span className="text-[#C9A96E] text-[10px] uppercase tracking-[0.35em] font-light">
                        {t("Experience", { de: "Erleben Sie" })} {proposal.hotel_name}
                      </span>
                    </div>
                    {/* Video play/pause toggle */}
                    <button 
                      onClick={() => {
                        const video = document.querySelector('video');
                        if (video) {
                          if (isVideoPlaying) video.pause();
                          else video.play();
                          setIsVideoPlaying(!isVideoPlaying);
                        }
                      }}
                      className="absolute bottom-24 right-6 p-3 glass-surface rounded-full hover:bg-white/20 transition-colors z-20"
                    >
                      {isVideoPlaying ? <Pause className="w-5 h-5 text-white" /> : <Play className="w-5 h-5 text-white" />}
                    </button>
                  </div>
                ) : (
                  <img 
                    src={heroImages[currentImageIndex]} 
                    alt={proposal.hotel_name}
                    className="w-full h-full object-cover"
                    style={{ transform: 'scale(1.1)' }}
                  />
                )}
              </motion.div>
            </AnimatePresence>
            
            {/* Cinematic gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-[#050505]/20" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/50 via-transparent to-[#050505]/50" />
          </motion.div>

          {/* Navigation Arrows */}
          {heroImages.length > 1 && (
            <>
              <button 
                onClick={prevImage}
                className="absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 p-4 glass-surface rounded-full hover:bg-white/20 transition-all hover:scale-110 z-10"
              >
                <ChevronLeft className="w-6 h-6 text-white" />
              </button>
              <button 
                onClick={nextImage}
                className="absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 p-4 glass-surface rounded-full hover:bg-white/20 transition-all hover:scale-110 z-10"
              >
                <ChevronRight className="w-6 h-6 text-white" />
              </button>
            </>
          )}

          {/* Image Counter / Progress Bar */}
          {heroImages.length > 1 && (
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-10">
              {heroImages.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentImageIndex(i)}
                  className={`h-1.5 rounded-full transition-all duration-500 ${
                    i === currentImageIndex ? "bg-[#C9A96E] w-10" : "bg-white/30 w-6 hover:bg-white/50"
                  }`}
                />
              ))}
            </div>
          )}

          {/* Hero Content Overlay with animation */}
          <motion.div 
            style={{ opacity: heroOpacity }}
            className="absolute bottom-0 left-0 right-0 p-6 lg:p-12 z-10"
          >
            <div className="max-w-7xl mx-auto">
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.8 }}
                className="flex items-center gap-3 mb-4"
              >
                {proposal.logo_url ? (
                  <img src={proposal.logo_url} alt="Logo" className="h-12 w-auto object-contain drop-shadow-lg" />
                ) : (
                  <Sparkles className="w-8 h-8 text-[#C9A96E]" />
                )}
                <span className="text-white/80 text-sm uppercase tracking-[0.3em] font-light">
                  {t("Exclusive Partnership", "Exklusive Partnerschaft")}
                </span>
              </motion.div>
              <motion.h1 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.8 }}
                className="font-display text-4xl lg:text-6xl text-white mb-3 drop-shadow-lg"
                style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif" }}
              >
                {proposal.hotel_name}
              </motion.h1>
              {proposal.location && (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7, duration: 0.8 }}
                  className="flex items-center gap-2 text-white/70"
                >
                  <MapPin className="w-5 h-5" />
                  <span className="text-lg">{proposal.location}</span>
                </motion.div>
              )}
            </div>
          </motion.div>
        </section>
      )}

      {/* Main Content */}
      <div className="relative z-10 px-4 py-8 lg:px-8 lg:py-12">
        <div className="max-w-7xl mx-auto">
          
          {/* Header (if no hero images) */}
          {!hasImages && (
            <motion.header
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-12"
            >
              <div className="flex items-center justify-center gap-2 mb-4">
                {proposal.logo_url ? (
                  <img src={proposal.logo_url} alt="Logo" className="h-12 w-auto object-contain" />
                ) : (
                  <Sparkles className="w-6 h-6 text-[#C9A96E]" />
                )}
              </div>
              <h1 className="font-display text-3xl lg:text-5xl text-white mb-3" style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif" }}>
                {t("Exclusive Partnership", "Exklusive Partnerschaft")}
              </h1>
              <h2 className="text-2xl lg:text-4xl text-transparent bg-clip-text bg-gradient-to-r from-[#C9A96E] to-[#D4B87A]" style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif" }}>
                {proposal.hotel_name}
              </h2>
              {proposal.location && (
                <div className="flex items-center justify-center gap-2 mt-4 text-white/50">
                  <MapPin className="w-4 h-4" />
                  <span>{proposal.location}</span>
                </div>
              )}
            </motion.header>
          )}

          {/* PERSONALIZED MESSAGE SECTION */}
          {getPersonalizedMessage() && (
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mb-16"
              data-testid="personalized-message-section"
            >
              <div className="glass-card-static p-6 lg:p-10 border-l-2 border-[#C9A96E]/30">
                <div className="flex items-start gap-5">
                  <div className="w-10 h-10 rounded-full bg-[#C9A96E]/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <Sparkles className="w-5 h-5 text-[#C9A96E]" />
                  </div>
                  <div>
                    <h3 className="text-xl text-white/90 mb-3" style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif" }}>
                      {t("Dear", { de: "Liebes", fr: "Cher", es: "Querido", it: "Caro", th: "เรียน" })} {proposal.hotel_name} {t("Team", { de: "Team", fr: "Équipe", es: "Equipo", it: "Team", th: "ทีม" })},
                    </h3>
                    <p className="text-white/50 leading-relaxed text-base font-light whitespace-pre-line">
                      {getPersonalizedMessage()}
                    </p>
                  </div>
                </div>
              </div>
            </motion.section>
          )}

          {/* Image Gallery Thumbnails (if multiple images) */}
          {heroImages.length > 3 && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-10"
            >
              <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                {heroImages.slice(0, 6).map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentImageIndex(i)}
                    className={`aspect-video rounded-lg overflow-hidden border-2 transition-all ${
                      i === currentImageIndex ? "border-[#C9A96E]" : "border-transparent opacity-60 hover:opacity-100"
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Countdown Timer */}
          {proposal.offer_end && !isExpired && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="flex justify-center mb-10"
            >
              <div className="countdown-box">
                <Clock className="w-5 h-5 text-[#C9A96E]" />
                <span className="text-white/70 text-sm">{t("Offer ends in", "Angebot endet in")}</span>
                <div className="flex gap-4">
                  {[
                    { value: countdown.days, label: t("Days", "Tage") },
                    { value: countdown.hours, label: t("Hrs", "Std") },
                    { value: countdown.minutes, label: "Min" },
                    { value: countdown.seconds, label: "Sec" }
                  ].map((item, i) => (
                    <div key={i} className="text-center">
                      <div className="countdown-digit">{String(item.value).padStart(2, '0')}</div>
                      <div className="countdown-label">{item.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* PRICING SECTION */}
          <motion.section
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-20"
            data-testid="pricing-section"
          >
            <h3 className="text-2xl text-white text-center mb-10" style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif" }}>
              {t("Choose Your Plan", "Wählen Sie Ihren Plan")}
            </h3>
            
            <div className="pricing-grid">
              {plans.map((item, index) => {
                const isSelected = selectedPlan === item.key;
                const isPopular = item.plan.is_popular;
                
                return (
                  <motion.div
                    key={item.key}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 * index }}
                    onClick={() => !isExpired && setSelectedPlan(item.key)}
                    className={`glass-card pricing-card cursor-pointer ${isPopular ? 'popular' : ''} ${
                      isSelected ? 'ring-1 ring-[#C9A96E]/50' : ''
                    }`}
                    data-testid={`plan-${item.key}-card`}
                  >
                    {/* Popular Badge */}
                    {isPopular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="px-4 py-1.5 bg-[#C9A96E] text-[#030303] text-xs font-semibold rounded-full whitespace-nowrap uppercase tracking-wider">
                          {t("Recommended", "Empfohlen")}
                        </span>
                      </div>
                    )}
                    
                    {/* Plan Name */}
                    <div className="text-center mb-4 pt-2">
                      <span className="inline-flex items-center gap-2 px-3 py-1 text-xs tracking-[0.2em] text-white/50 font-medium uppercase">
                        {item.plan.name}
                        <InfoPopover
                          title={item.plan.name}
                          descriptionEn={item.plan.description_en}
                          descriptionDe={item.plan.description_de}
                          videoUrl={item.plan.info_video_url}
                          language={language}
                        />
                      </span>
                    </div>
                    
                    {/* Price */}
                    <div className="text-center mb-4">
                      <span className="text-3xl font-light text-white" style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif" }}>{"\u20AC"}{(item.plan.price || 0).toLocaleString('de-DE')}</span>
                      <span className="text-white/30 text-sm ml-1">/{t("year", "Jahr")}</span>
                    </div>
                    
                    {/* CTA Button */}
                    <button
                      disabled={isExpired}
                      className={`w-full py-3 rounded-xl font-medium mb-6 transition-all duration-300 ${
                        isSelected
                          ? isPopular ? 'bg-[#C9A96E] text-[#030303]' : 'bg-white/90 text-[#030303]'
                          : isPopular ? 'bg-[#C9A96E]/10 text-[#C9A96E] hover:bg-[#C9A96E]/20' : 'bg-white/5 text-white/70 hover:bg-white/10'
                      }`}
                      data-testid={`select-plan-${item.key}-btn`}
                    >
                      {isSelected ? t("Selected", "Ausgewählt") : t("Choose", "Wählen")}
                    </button>
                    
                    {/* Features */}
                    <div className="features">
                      {(item.plan.features || []).map((feature, i) => {
                        const featureDef = (proposal.feature_definitions || []).find(f => f.name === feature);
                        return (
                          <div key={i} className="feature-item">
                            <Check className="feature-icon w-4 h-4 shrink-0" />
                            <span className="flex-1">{feature}</span>
                            <InfoPopover
                              title={feature}
                              descriptionEn={featureDef?.description_en}
                              descriptionDe={featureDef?.description_de}
                              videoUrl={featureDef?.info_video_url}
                              language={language}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.section>

          {/* OPTIONAL EXTENSIONS SECTION — Premium Cards */}
          {(addOns.length > 0) && (
            <motion.section
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mb-20"
              data-testid="addons-section"
            >
              <div className="text-center mb-10">
                <p className="text-[#C9A96E] text-[10px] uppercase tracking-[0.35em] font-light mb-3">
                  {t("Optional Extensions", { de: "Optionale Erweiterungen" })}
                </p>
                <h3 className="text-2xl text-white" style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif" }}>
                  {t("Extend your presence.", { de: "Erweitern Sie Ihre Präsenz." })}
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {addOns.filter(addon =>
                  !selectedPlan || (addon.eligible_plans || []).includes(selectedPlan)
                ).map((addon) => {
                  const isAddonSelected = selectedAddOns.find(a => a.id === addon.id && !a.package_id);
                  const addonName = language === "en" ? addon.name_en : (addon.name_de || addon.name_en);
                  const addonDesc = language === "en" ? addon.description_en : (addon.description_de || addon.description_en);
                  const addonNote = language === "en" ? addon.note_en : (addon.note_de || addon.note_en);
                  const includes = addon.includes || [];

                  return (
                    <button
                      key={addon.id}
                      onClick={() => !isExpired && toggleAddOn(addon)}
                      className={`relative text-left p-6 rounded-2xl transition-all duration-400 ${
                        isAddonSelected
                          ? 'bg-[#C9A96E]/[0.06] border border-[#C9A96E]/30 ring-1 ring-[#C9A96E]/20'
                          : 'bg-white/[0.02] border border-white/[0.06] hover:border-white/[0.12] hover:bg-white/[0.04]'
                      }`}
                      data-testid={`addon-select-${addon.id}`}
                    >
                      {/* Header row: name + toggle */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-white/80 text-sm font-medium tracking-wide">{addonName}</p>
                          <p className="text-white/30 text-[13px] mt-1 leading-relaxed font-light">{addonDesc}</p>
                        </div>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ml-4 transition-all ${
                          isAddonSelected ? 'bg-[#C9A96E]' : 'bg-white/[0.06] border border-white/[0.08]'
                        }`}>
                          {isAddonSelected && <Check className="w-3.5 h-3.5 text-[#030303]" />}
                        </div>
                      </div>

                      {/* Price range */}
                      <div className="mb-4">
                        <span className="text-white/70 text-lg font-light" style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif" }}>
                          {"\u20AC"}{addon.price_range_label || (addon.price || 0).toLocaleString('de-DE')}
                        </span>
                      </div>

                      {/* Includes list */}
                      {includes.length > 0 && (
                        <div className="border-t border-white/[0.05] pt-3 space-y-1.5">
                          {includes.map((item, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <Check className={`w-3 h-3 shrink-0 ${isAddonSelected ? 'text-[#C9A96E]/60' : 'text-white/20'}`} />
                              <span className="text-white/40 text-[12px] font-light">{item}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Note */}
                      {addonNote && (
                        <p className="text-white/20 text-[11px] mt-3 italic font-light">{addonNote}</p>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Notes section — awards disclaimer */}
              {(proposal.notes_en || proposal.notes_de) && (
                <div className="mt-10 border-l-2 border-[#C9A96E]/20 pl-6 py-2">
                  <p className="text-white/30 text-[13px] leading-relaxed font-light whitespace-pre-line">
                    {language === "en" ? proposal.notes_en : (proposal.notes_de || proposal.notes_en)}
                  </p>
                </div>
              )}
            </motion.section>
          )}

          {/* INVESTMENT SUMMARY */}
          <motion.section
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mb-16"
            data-testid="checkout-section"
          >
            <div className="glass-card-static p-6 lg:p-10">
              <p className="text-[#C9A96E] text-[10px] uppercase tracking-[0.35em] font-light mb-2">
                {t("Investment Summary", { de: "Investitions\u00fcbersicht" })}
              </p>
              <h3 className="text-xl text-white mb-6" style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif" }}>
                {t("Your Selection", { de: "Ihre Auswahl", fr: "Votre s\u00e9lection", es: "Su selecci\u00f3n", it: "La tua selezione", th: "\u0E01\u0E32\u0E23\u0E40\u0E25\u0E37\u0E2D\u0E01\u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13" })}
              </h3>
              
              {/* Itemized breakdown */}
              <div className="space-y-3 mb-6" data-testid="selection-breakdown">
                {/* Selected Plan */}
                {selectedPlan && (() => {
                  const planKey = `plan_${selectedPlan.toLowerCase()}`;
                  const planData = proposal?.[planKey];
                  return (
                    <div className="flex items-center justify-between py-3 border-b border-white/[0.06]" data-testid="selection-plan">
                      <span className="text-white font-medium tracking-wide text-sm">
                        {planData?.name || `Plan ${selectedPlan}`}
                      </span>
                      <span className="text-white/80 text-sm" style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif" }}>
                        {"\u20AC"}{(planData?.price || 0).toLocaleString('de-DE')}
                      </span>
                    </div>
                  );
                })()}
                
                {/* Selected Add-Ons */}
                {selectedAddOns.map(addon => {
                  const addonName = language === "en" ? addon.name_en : (addon.name_de || addon.name_en);
                  return (
                    <div key={addon.id} className="flex items-center justify-between py-3 border-b border-white/[0.06]" data-testid={`selection-addon-${addon.id}`}>
                      <span className="text-white/50 text-sm font-light flex items-center gap-2">
                        <Plus className="w-3 h-3 text-[#C9A96E]/50 shrink-0" />
                        {addonName}
                      </span>
                      <span className="text-white/50 text-sm shrink-0 ml-4" style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif" }}>
                        +{"\u20AC"}{(addon.price || 0).toLocaleString('de-DE')}
                      </span>
                    </div>
                  );
                })}
              </div>
              
              {/* Discount notice */}
              {proposal.discount_percent > 0 && (
                <div className="flex items-center justify-between py-3 border-b border-white/[0.06] mb-6">
                  <span className="text-[#10B981] text-sm">
                    {t(`${proposal.discount_percent}% discount with code:`, { de: `${proposal.discount_percent}% Rabatt mit Code:` })} {proposal.discount_code}
                  </span>
                  <span className="text-[#10B981] text-sm">
                    -{"\u20AC"}{(() => {
                      if (!selectedPlan) return 0;
                      const planKey = `plan_${selectedPlan.toLowerCase()}`;
                      const planPrice = proposal?.[planKey]?.price || 0;
                      const addOnsPrice = selectedAddOns.reduce((sum, a) => sum + (a.price || 0), 0);
                      const subtotal = planPrice + addOnsPrice;
                      return Math.round(subtotal * proposal.discount_percent / 100).toLocaleString('de-DE');
                    })()}
                  </span>
                </div>
              )}
              
              {/* Total and CTA */}
              <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 pt-4">
                <div>
                  <p className="text-white/25 text-[10px] uppercase tracking-[0.2em] mb-2">{t("Total", { de: "Gesamt", fr: "Total", es: "Total" })}</p>
                  <p className="text-3xl lg:text-4xl text-white font-light" style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif" }} data-testid="total-price">
                    {"\u20AC"}{Number(calculateTotal()).toLocaleString('de-DE')}
                    <span className="text-white/25 text-lg ml-1">/{t("yr", { de: "Jahr", fr: "an", es: "a\u00f1o" })}</span>
                  </p>
                </div>
                
                <button
                  onClick={handleCheckout}
                  disabled={isExpired || !selectedPlan}
                  className={`inline-flex items-center gap-3 px-8 py-3.5 bg-[#C9A96E] text-[#030303] rounded-xl text-sm font-medium tracking-wide hover:bg-[#D4B87A] transition-colors duration-300 ${
                    (isExpired || !selectedPlan) ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  data-testid="checkout-btn"
                >
                  {isExpired 
                    ? t("Expired", { de: "Abgelaufen" })
                    : (language === "en" ? proposal.cta_text_en : proposal.cta_text_de) || t("Proceed to Partnership Setup", { de: "Weiter zur Partnerschafts-Einrichtung" })
                  }
                </button>
              </div>
            </div>
          </motion.section>

          {/* Footer */}
          <motion.footer
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-center py-8 border-t border-white/5"
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              {proposal.logo_url ? (
                <img src={proposal.logo_url} alt="Logo" className="h-8 w-auto object-contain opacity-60" />
              ) : (
                <Sparkles className="w-5 h-5 text-[#C9A96E]" />
              )}
              <span className="font-display text-lg text-white">{proposal.hotel_name}</span>
            </div>
            
            <div className="flex items-center justify-center gap-4 mb-4">
              {proposal.email && (
                <a href={`mailto:${proposal.email}`} className="text-white/40 hover:text-white transition-colors">
                  <Mail className="w-5 h-5" />
                </a>
              )}
              {proposal.phone && (
                <a href={`tel:${proposal.phone}`} className="text-white/40 hover:text-white transition-colors">
                  <Phone className="w-5 h-5" />
                </a>
              )}
              {proposal.social_links?.instagram && (
                <a href={proposal.social_links.instagram} target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-white transition-colors">
                  <Instagram className="w-5 h-5" />
                </a>
              )}
              {proposal.social_links?.facebook && (
                <a href={proposal.social_links.facebook} target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-white transition-colors">
                  <Facebook className="w-5 h-5" />
                </a>
              )}
            </div>
            
            <p className="text-white/30 text-xs">
              Powered by Soulful Places • {proposal.proposal_id}
            </p>
          </motion.footer>
        </div>
      </div>
    </div>
  );
}
